/**
 * useNewDevices - Hook for managing new/unmatched devices
 * Provides CRUD operations and state management for devices from Starlink/Sol-Ark
 * that don't have matching units in the database
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authenticatedFetch } from '@/lib/api';

const API_BASE = '/api/v1/admin/new-devices';

export type DeviceSource = 'starlink' | 'solark' | 'omni' | 'tesla' | 'hvac';
export type DeviceStatus = 'pending' | 'linked' | 'dismissed' | 'created';

export interface NewDevice {
  id: string;
  source: DeviceSource;
  external_id: string;
  external_name: string | null;
  suggested_unit_code: string | null;
  device_metadata: Record<string, unknown>;
  status: DeviceStatus;
  linked_unit_id: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  first_seen_at: string;
  last_seen_at: string;
  last_comms_at: string | null;
  seen_count: number;
  created_at: string;
  updated_at: string;
}

export interface DeviceCounts {
  starlink: number;
  solark: number;
  omni: number;
  tesla: number;
  hvac: number;
  total: number;
}

export interface UnitSuggestion {
  id: string;
  unit_code: string;
  name: string | null;
  similarity: 'exact' | 'prefix6' | 'prefix3';
}

interface UseNewDevicesOptions {
  autoFetch?: boolean;
  status?: DeviceStatus;
  source?: DeviceSource;
  pageSize?: number;
}

export interface LinkDeviceOptions {
  primaryGroupId?: string | null;
}

export interface BulkApprovalPreviewRow {
  id: string;
  source: DeviceSource;
  externalId: string;
  externalName: string | null;
  proposedUnitCode: string | null;
  status: 'ready_create' | 'ready_link' | 'conflict';
  targetUnitId?: string | null;
  targetCompanyId?: string | null;
  targetPrimaryGroupId?: string | null;
  reason: string | null;
}

export interface BulkApprovalPreview {
  rows: BulkApprovalPreviewRow[];
  total: number;
  ready: number;
  conflicts: number;
}

export interface BulkApprovalRequest {
  deviceIds: string[];
  companyId: string;
  primaryGroupId?: string | null;
  industryType?: string;
}

export interface BulkApprovalResultRow {
  deviceId: string;
  externalId?: string;
  externalName?: string | null;
  proposedUnitCode?: string | null;
  unitId?: string;
  companyId?: string;
  primaryGroupId?: string | null;
  status: 'created' | 'linked' | 'skipped' | 'error';
  reason?: string;
  physicalLinked?: boolean;
}

export interface BulkApprovalResult {
  success: boolean;
  approved: number;
  created: number;
  linked: number;
  skipped: number;
  failed: number;
  results: BulkApprovalResultRow[];
}

interface UseNewDevicesReturn {
  // Data
  devices: NewDevice[];
  totalCount: number;
  counts: DeviceCounts;
  page: number;
  pageSize: number;

  // Loading states
  isLoading: boolean;
  isLoadingCounts: boolean;
  isSubmitting: boolean;
  isBulkApproving: boolean;

  // Error state
  error: string | null;

  // Actions
  fetchDevices: (options?: { page?: number; status?: DeviceStatus; source?: DeviceSource }) => Promise<void>;
  fetchCounts: () => Promise<void>;
  linkDevice: (deviceId: string, unitId: string, options?: LinkDeviceOptions) => Promise<boolean>;
  dismissDevice: (deviceId: string, notes?: string) => Promise<boolean>;
  markCreated: (deviceId: string, unitId: string) => Promise<boolean>;
  getSuggestions: (deviceId: string) => Promise<UnitSuggestion[]>;
  getBulkApprovalPreview: () => Promise<BulkApprovalPreview>;
  approvePending: (request: BulkApprovalRequest) => Promise<BulkApprovalResult>;
  setPage: (page: number) => void;
  refresh: () => Promise<void>;
}

export function useNewDevices(options: UseNewDevicesOptions = {}): UseNewDevicesReturn {
  const {
    autoFetch = true,
    status: defaultStatus = 'pending',
    source: defaultSource,
    pageSize: defaultPageSize = 50,
  } = options;

  const [devices, setDevices] = useState<NewDevice[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState<DeviceCounts>({ starlink: 0, solark: 0, omni: 0, tesla: 0, hvac: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(defaultPageSize);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<{ status?: DeviceStatus; source?: DeviceSource }>({
    status: defaultStatus,
    source: defaultSource,
  });

  const fetchDevices = useCallback(async (fetchOptions?: { page?: number; status?: DeviceStatus; source?: DeviceSource }) => {
    setIsLoading(true);
    setError(null);

    const targetPage = fetchOptions?.page ?? page;
    const status = fetchOptions?.status ?? currentFilters.status;
    const source = fetchOptions?.source ?? currentFilters.source;

    // Update filters if changed
    if (fetchOptions?.status !== undefined || fetchOptions?.source !== undefined) {
      setCurrentFilters({ status, source });
    }

    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String((targetPage - 1) * pageSize));
      if (status) params.set('status', status);
      if (source) params.set('source', source);

      const response = await authenticatedFetch(`${API_BASE}?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setDevices(data.data || []);
      setTotalCount(data.count || 0);
      setPage(targetPage);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch devices';
      setError(message);
      console.error('[useNewDevices] fetchDevices error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, currentFilters]);

  const fetchCounts = useCallback(async () => {
    setIsLoadingCounts(true);

    try {
      const response = await authenticatedFetch(`${API_BASE}/count`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCounts(data);
    } catch (err) {
      console.error('[useNewDevices] fetchCounts error:', err);
    } finally {
      setIsLoadingCounts(false);
    }
  }, []);

  const linkDevice = useCallback(async (deviceId: string, unitId: string, options?: LinkDeviceOptions): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const requestBody: { unitId: string; primaryGroupId?: string | null } = { unitId };
      if (options?.primaryGroupId !== undefined) {
        requestBody.primaryGroupId = options.primaryGroupId;
      }

      const response = await authenticatedFetch(`${API_BASE}/${deviceId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to link device');
      }

      toast.success('Device linked to unit');
      await fetchDevices();
      await fetchCounts();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link device';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchDevices, fetchCounts]);

  const dismissDevice = useCallback(async (deviceId: string, notes?: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${deviceId}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to dismiss device');
      }

      toast.success('Device dismissed');
      await fetchDevices();
      await fetchCounts();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to dismiss device';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchDevices, fetchCounts]);

  const markCreated = useCallback(async (deviceId: string, unitId: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_BASE}/${deviceId}/mark-created`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unitId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to mark device as created');
      }

      toast.success('Unit created and device linked');
      await fetchDevices();
      await fetchCounts();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark device as created';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchDevices, fetchCounts]);

  const getSuggestions = useCallback(async (deviceId: string): Promise<UnitSuggestion[]> => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/${deviceId}/suggestions`);

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (err) {
      console.error('[useNewDevices] getSuggestions error:', err);
      return [];
    }
  }, []);

  const getBulkApprovalPreview = useCallback(async (): Promise<BulkApprovalPreview> => {
    const response = await authenticatedFetch(`${API_BASE}/bulk-approval-preview`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to preview pending units');
    }
    return response.json();
  }, []);

  const approvePending = useCallback(async (request: BulkApprovalRequest): Promise<BulkApprovalResult> => {
    setIsBulkApproving(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_BASE}/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to approve pending units');
      }

      const result = await response.json() as BulkApprovalResult;
      await Promise.all([fetchDevices(), fetchCounts()]);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve pending units';
      setError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsBulkApproving(false);
    }
  }, [fetchDevices, fetchCounts]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchDevices(), fetchCounts()]);
  }, [fetchDevices, fetchCounts]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchDevices();
      fetchCounts();
    }
  }, [autoFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    devices,
    totalCount,
    counts,
    page,
    pageSize,
    isLoading,
    isLoadingCounts,
    isSubmitting,
    isBulkApproving,
    error,
    fetchDevices,
    fetchCounts,
    linkDevice,
    dismissDevice,
    markCreated,
    getSuggestions,
    getBulkApprovalPreview,
    approvePending,
    setPage,
    refresh,
  };
}

export default useNewDevices;
